class ProductGrid extends HTMLElement {
  constructor() {
    super();
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleSearchInput = this.handleSearchInput.bind(this);
  }

  get sectionsToReplace() {
    return this.dataset.sectionsToReplace
      ? this.dataset.sectionsToReplace.split(",").map((s) => s.trim())
      : [];
  }

  get sectionsToUpdate() {
    return this.dataset.sectionsToUpdate
      ? this.dataset.sectionsToUpdate.split(",").map((s) => s.trim())
      : [];
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  connectedCallback() {
    // product grid
    this.bosProductGrid = this.querySelector(".bosProductGrid");

    // product grid counter
    this.bosProductGridCountCurrent = this.querySelector(
      ".bosProductGridCountCurrent",
    );

    // editorial content
    this.editorialData = this.extractEditorialJson();
    if (this.editorialData) {
      this.getEditorialContent(this.editorialData);
    }

    this.counterUpdate();

    this.bindEvents();

    document.addEventListener("filters-drawer:updated", () => {
      requestAnimationFrame(() => {
        // re-cache internal refs since DOM was replaced
        this.bosProductGrid = this.querySelector(".bosProductGrid");
        this.bosProductGridCountCurrent = this.querySelector(
          ".bosProductGridCountCurrent",
        );

        this.bindEvents();
        this.counterUpdate();

        // re-inject editorials if needed
        if (this.editorialData) {
          this.getEditorialContent(this.editorialData);
        }
      });
    });

    document.addEventListener("sort-drawer:updated", () => {
      requestAnimationFrame(() => {
        this.bosProductGrid = this.querySelector(".bosProductGrid");
        this.bosProductGridCountCurrent = this.querySelector(
          ".bosProductGridCountCurrent",
        );
        this.bindEvents();
        this.counterUpdate();
      });
    });
  }

  disconnectedCallback() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener("click", this.handleLoadMore);
    }
    // Add this 👇
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
    }
  }

  getProductElements() {
    const bosProductGridProduct = document.querySelectorAll(
      ".bosProductGridProduct",
    );
    const bosProductGridCountCurrent = document.querySelector(
      ".bosProductGridCountCurrent",
    );
    if (bosProductGridCountCurrent) {
      bosProductGridCountCurrent.innerHTML = bosProductGridProduct.length;
    }
  }

  bindEvents() {
    this.initScrollReveal();
    // load more
    this.loadMoreBtn = this.querySelector(".bosProductGridLoadMoreButton");
    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener("click", this.handleLoadMore);
      this.loadMoreBtn.addEventListener("click", this.handleLoadMore);
    }

    this.bosSearchHeaderInput = document.querySelector(".bosSearchHeaderInput");
    if (this.bosSearchHeaderInput) {
      this.bosSearchHeaderInput.removeEventListener(
        "keydown",
        this.handleSearchInput,
      );
      this.bosSearchHeaderInput.addEventListener(
        "keydown",
        this.handleSearchInput,
      );
    }
  }

  initScrollReveal() {
    // Kill any existing observer first
    if (this._scrollObserver) {
      this._scrollObserver.disconnect();
    }

    this._scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            this._scrollObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    this.querySelectorAll(
      ".bosProductGridProduct:not(.is-visible), .bosProductGridEditorialContent:not(.is-visible), .bosProductGridEditorialContentSpanOne:not(.is-visible)",
    ).forEach((el) => this._scrollObserver.observe(el));
  }

  handleSearchInput(e) {
    if (e.key !== "Enter") return;

    const target = e.target;
    let targetValue = target.value;

    if (targetValue.length > 2) {
      const url = new URL(window.location.href);

      url.searchParams.set("sections", this.sectionId);
      url.searchParams.set("q", targetValue);

      url.searchParams.delete("page");

      const displayUrl = new URL(window.location.href);
      displayUrl.searchParams.set("q", targetValue);

      history.pushState({}, "", displayUrl.toString());

      this.fetchUpdatedContent(url);
    }
  }

  counterUpdate() {
    if (!this.bosProductGrid) return;
    const excluded = [
      "bosProductGridEditorialContent",
      "bosProductGridEditorialContentSpanOne",
      "bosProductGridEditorialContentSpanTwo",
      "bosProductGridEditorialContentSpanThree",
    ];
    const numberOfElements = [
      ...this.querySelectorAll(".bosProductGridProduct"),
    ].filter(
      (el) => !excluded.some((cls) => el.classList.contains(cls)),
    ).length;
    if (!this.bosProductGridCountCurrent) return;

    this.bosProductGridCountCurrent.textContent = `${numberOfElements}`;
  }

  handleLoadMore(e) {
    e.preventDefault();
    const target = e.currentTarget;

    if (target && this.sectionId) {
      const newUrl = target.href;
      const url = new URL(newUrl);
      url.searchParams.set("sections", this.sectionId);

      const searchQuery = new URL(window.location.href).searchParams.get("q");
      if (searchQuery) {
        url.searchParams.set("q", searchQuery);
      }
      this.fetchUpdatedContent(url);
    }
  }

  fetchUpdatedContent(url) {
    fetch(url.toString())
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        this.renderSection(
          data[this.sectionId],
          this.getSectionsToUpdate(this.sectionId),
        );

        // rebind listener
        this.bindEvents();
        // update counter

        // notify other elements that the grid has updated
        document.dispatchEvent(new CustomEvent("product-grid:updated"));

        requestAnimationFrame(() => {
          this.counterUpdate();
          this.getProductElements();
        });
      })
      .catch((err) => console.error(err));
  }

  renderSection(html, selectors) {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = temp.querySelector(selector);

      if (!target || !source) return;

      // add to end of products
      if (selector === ".bosProductGrid") {
        target.insertAdjacentHTML("beforeend", source.innerHTML);
        return;
      }

      // else just update
      target.innerHTML = source.innerHTML;
    });
  }

  getSectionsToUpdate(sectionId) {
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!section) return [];

    const sectionsToUpdate = section.dataset.sectionsToUpdate;
    if (!sectionsToUpdate) return [];

    return sectionsToUpdate
      .split(",")
      .map((s) => s.trim())
      .map((s) => `.${s}`);
  }

  // Editorial Content Start
  extractEditorialJson() {
    const rawContent = document.querySelector("#bosProductGridEditorialJson");
    if (!rawContent) return null;

    try {
      const parsedContent = JSON.parse(rawContent.textContent.trim());
      if (parsedContent) {
        return parsedContent;
      }
    } catch (error) {
      console.error("Invalid JSON:", error);
      return null;
    }
  }

  getEditorialContent(data) {
    const { editorials } = data;
    if (!Array.isArray(editorials)) return;

    const productGrid = this.bosProductGrid;
    if (!productGrid) return;

    const sorted = [...editorials].sort((a, b) => a.position - b.position);

    sorted.forEach((editorialData) => {
      const { position, span, image } = editorialData;

      if (!image) return;

      // get span number
      const spanNum = Number(span);
      const targetIndex = position - 1;

      const products = Array.from(
        productGrid.querySelectorAll(".bosProductGridProduct"),
      );

      if (targetIndex >= products.length) return;

      const alreadyInserted = productGrid.querySelector(
        `[data-editorial-position="${position}"]`,
      );
      if (alreadyInserted) return;

      // wrapper
      const editorial = this.createEditorialWrapper(position, spanNum);

      // wrapper elements
      this.createEditorialImage(editorial, image);
      this.createEditorialTextContent(editorial, editorialData);

      const referenceNode = products[targetIndex];
      productGrid.insertBefore(editorial, referenceNode);
    });
  }

  createEditorialWrapper(position, spanNum) {
    const editorial = document.createElement("div");

    editorial.dataset.editorialPosition = position;
    editorial.dataset.editorialSpan = spanNum;

    editorial.classList.add(
      spanNum === 1
        ? "bosProductGridEditorialContentSpanOne"
        : "bosProductGridEditorialContent",
    );

    return editorial;
  }

  createEditorialImage(editorial, image) {
    const img = document.createElement("img");
    img.src = image;
    img.alt = "";

    editorial.appendChild(img);
  }

  createEditorialTextContent(editorial, data) {
    const { subtitle, title, button_text, button_link } = data;

    const content = document.createElement("div");
    content.classList.add("bosProductGridEditorialContentText");

    // subtitle
    if (subtitle) {
      const p = document.createElement("p");
      p.classList.add("bosFontSubtitle");
      p.textContent = subtitle;
      content.appendChild(p);
    }

    // title
    if (title) {
      const h4 = document.createElement("h4");
      h4.textContent = title;
      h4.classList.add("bosFontTitle");
      content.appendChild(h4);
    }

    // link
    if (button_text && button_link) {
      const a = document.createElement("a");
      a.href = button_link;
      a.textContent = button_text;
      content.appendChild(a);
    }

    editorial.appendChild(content);
  }
  // Editorial Content End
}

if (!customElements.get("product-grid")) {
  customElements.define("product-grid", ProductGrid);
}
